import { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import logError from '@alltrails/core/utils/logError';
import { useLocalStorage } from '@alltrails/local-storage';
import { updateHiddenPOIs } from '../redux/reducer';
import { hidePOI } from '../requests/hidePOI';

type UseHidePOIParams = {
  ID: number;
  hiddenPOIs?: number[];
};

const useHidePOI = ({ ID, hiddenPOIs }: UseHidePOIParams) => {
  const dispatch = useDispatch();
  const [isHiding, setIsHiding] = useState(false);
  const [showHideConfirmation, setShowHideConfirmation] = useState(false);
  const { setValue, getValue } = useLocalStorage<number[]>('hiddenPOIs');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const persistedHiddenPOIs = getValue();
      if (persistedHiddenPOIs) {
        dispatch(updateHiddenPOIs(persistedHiddenPOIs));
      }
    }
  }, [dispatch]);

  const onHidePOI = useCallback(
    async (e?: React.MouseEvent<HTMLElement>) => {
      e?.preventDefault();

      setIsHiding(true);
      try {
        const response = await hidePOI(ID);
        if (response.status === 'ok') {
          const hidden = [...(hiddenPOIs || []), ID];
          dispatch(updateHiddenPOIs(hidden));
          if (typeof window !== 'undefined') {
            setValue(hidden);
          }
        }
        setIsHiding(false);
        setShowHideConfirmation(false);
      } catch (error) {
        logError(new Error(`Error when hiding the POI: ${error}`));
        setIsHiding(false);
      }
    },
    [dispatch, hiddenPOIs, ID]
  );

  return {
    isHiding,
    showHideConfirmation,
    setShowHideConfirmation,
    onHidePOI
  };
};

export default useHidePOI;
